/**
 * Gradient fills for React Native.
 *
 * The theme surface is CSS-shaped — `primaryColor` may be a solid hex OR a
 * `linear-gradient(...)` string (see core `types/theme.ts`, and the gradient
 * presets in `widget-theme`). The DOM renders those for free; RN has no
 * gradient primitive at all, so every themed surface goes through here.
 *
 * Colour PARSING is not duplicated — `isGradient` / `extractColorsFromGradient`
 * / `getDominantColor` come from `@ringg/core`, the same functions the web
 * widget uses (AGENTS.md rule 2: shared values come from core, never a local
 * re-implementation). Only the two things core cannot know about — the angle in
 * SVG coordinates, and how to paint it — live in this file.
 *
 * Non-linear gradients (radial/conic) degrade to their dominant colour rather
 * than approximating badly; the theme presets are all linear.
 */

import { Children, type FC, type ReactNode } from "react";
import { StyleSheet, View, type StyleProp, type ViewStyle } from "react-native";
import Svg, { Defs, LinearGradient as SvgLinearGradient, Rect, Stop } from "react-native-svg";
import { extractColorsFromGradient, getDominantColor, isGradient } from "@ringg/core";

interface GradientFillProps {
  /** A theme colour: solid (`#0a0a0b`, `rgb(...)`) or a CSS gradient string. */
  color: string;
  /** Applied to the wrapper — size, padding, radius, alignment. */
  style?: StyleProp<ViewStyle>;
  children?: ReactNode;
}

/** Direction of the gradient line in SVG space (y grows downward). */
interface GradientLine {
  x1: string;
  y1: string;
  x2: string;
  y2: string;
}

/** CSS `linear-gradient` default when no angle/side is given. */
const DEFAULT_ANGLE_DEG = 180;

const SIDE_ANGLES: Record<string, number> = {
  "to top": 0,
  "to right": 90,
  "to bottom": 180,
  "to left": 270,
  "to top right": 45,
  "to right top": 45,
  "to bottom right": 135,
  "to right bottom": 135,
  "to bottom left": 225,
  "to left bottom": 225,
  "to top left": 315,
  "to left top": 315,
};

/** `turn` / `rad` / `grad` / `deg` → degrees. Unknown units fall back. */
const angleToDegrees = (value: string): number | undefined => {
  const match = /^(-?\d+(?:\.\d+)?)(deg|rad|grad|turn)$/.exec(value.trim().toLowerCase());
  if (!match) return undefined;
  const amount = Number(match[1]);
  switch (match[2]) {
    case "deg":
      return amount;
    case "rad":
      return (amount * 180) / Math.PI;
    case "grad":
      return amount * 0.9;
    case "turn":
      return amount * 360;
    default:
      return undefined;
  }
};

/**
 * The first argument of a `linear-gradient` is the direction — an angle
 * (`135deg`) or a side (`to bottom right`) — and is optional. Anything else
 * means the gradient starts straight at a colour stop, so the CSS default
 * applies.
 */
const readAngle = (gradient: string): number => {
  const open = gradient.indexOf("(");
  const close = gradient.lastIndexOf(")");
  if (open === -1 || close <= open) return DEFAULT_ANGLE_DEG;
  const first = gradient.slice(open + 1, close).split(",")[0]?.trim().toLowerCase() ?? "";
  if (first.startsWith("to ")) return SIDE_ANGLES[first.replace(/\s+/g, " ")] ?? DEFAULT_ANGLE_DEG;
  return angleToDegrees(first) ?? DEFAULT_ANGLE_DEG;
};

/**
 * CSS angles are measured clockwise from "to top"; the gradient line runs
 * through the centre of the box. In screen coordinates (y down) the end
 * direction is `(sin A, -cos A)`, so the line spans centre ± half the vector.
 * Expressed in objectBoundingBox units, which is the same square-box
 * approximation the RN gradient libraries make.
 */
const angleToLine = (degrees: number): GradientLine => {
  const radians = (degrees * Math.PI) / 180;
  const dx = Math.sin(radians);
  const dy = -Math.cos(radians);
  return {
    x1: `${0.5 - dx / 2}`,
    y1: `${0.5 - dy / 2}`,
    x2: `${0.5 + dx / 2}`,
    y2: `${0.5 + dy / 2}`,
  };
};

/** Even distribution — the theme presets carry no explicit stop positions. */
const stopOffset = (index: number, total: number): string => (total <= 1 ? "0" : `${index / (total - 1)}`);

let gradientId = 0;
const nextGradientId = (): string => `ringg-gradient-${++gradientId}`;

/**
 * Paints `color` behind `children`. Solid colours take the cheap path
 * (`backgroundColor`); gradients get an absolutely-filled SVG layer that
 * inherits the wrapper's rounding through `overflow: hidden`.
 */
export const GradientFill: FC<GradientFillProps> = ({ color, style, children }) => {
  if (!isGradient(color)) return <View style={[{ backgroundColor: color }, style]}>{children}</View>;

  const colors = extractColorsFromGradient(color);
  // A gradient we can name but not read (`radial-gradient(...)`, unparsed
  // stops) still has to paint something — the dominant colour, same fallback
  // core documents for RN in `colorToBackground`.
  if (colors.length < 2) return <View style={[{ backgroundColor: getDominantColor(color) }, style]}>{children}</View>;

  const id = nextGradientId();
  const line = angleToLine(readAngle(color));

  return (
    <View style={[styles.clipped, style]}>
      <Svg style={StyleSheet.absoluteFill} pointerEvents="none">
        <Defs>
          <SvgLinearGradient id={id} x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2}>
            {colors.map((stop, index) => (
              <Stop key={`${stop}-${index}`} offset={stopOffset(index, colors.length)} stopColor={stop} />
            ))}
          </SvgLinearGradient>
        </Defs>
        <Rect x="0" y="0" width="100%" height="100%" fill={`url(#${id})`} />
      </Svg>
      {Children.count(children) > 0 ? children : null}
    </View>
  );
};

const styles = StyleSheet.create({
  clipped: { overflow: "hidden" },
});

/**
 * A guaranteed-solid colour, for the properties RN can only paint flat —
 * borders, shadows, icon tints, status dots.
 */
export const solidColor = (color: string): string => getDominantColor(color);
